export enum FieldRequestFieldChoices {
  ACCOUNTING_DATA = "accounting_data",
  SELF_ESTIMATED_REVENUE = "self_estimated_revenue",
  SELF_ESTIMATED_HISTORIC_REVENUE = "self_estimated_historic_revenue",
  GUARANTORS = "guarantors",
  BANKING_DATA = "banking_data",
  PROPERTY = "property",
  PROPERTY_ADDITIONAL_DATA = "property_additional_data",
  ADDITIONAL_SECURITIES = "additional_securities",
  UNPAID_INVOICES = "unpaid_invoices",
  UNPAID_INVOICES_ADDITIONAL_DATA = "unpaid_invoices_additional_data",
  COMPANY_MORTGAGE = "company_mortgage",
  BUDGET = "budget",
  REASON = "reason",
  REQUESTED_AMOUNT = "requested_amount",
  REPAYMENT_TIME = "repayment_time",
  SUGGESTED_AMOUNT = "suggested_amount",
  PAWNED_INVOICES = "pawned_invoices",
  PARENT_COMPANY_GUARANTEE = "parent_company_guarantee",
  SHARE_MORTGAGE = "share_mortgage",
  REFINANCING = "refinancing",
  SALE_OF_PROPERTY = "sale_of_property",
  PROPERTY_CASH_FLOW = "property_cash_flow",
  CONSTRUCTION_COMPLETED_REPAYMENT = "construction_completed_repayment",
  CONSTRUCTION_ESTIMATE = "construction_estimate",
  BUILDING_PERMIT = "building_permit",
  GROUPED_REAL_ESTATE_COLLATERAL = "grouped_real_estate_collateral",
  GROUPED_REAL_ESTATE_REPAYMENT = "grouped_real_estate_repayment",
  REFINANCING_AMOUNTS = "refinancing_amounts",
  LEASING = "leasing",
  LEASING_ADDITIONAL_DATA = "leasing_additional_data",
  REASON_EXPLANATION_FIELD = "reason_explanation",
  ANNUAL_REPORT = "annual_report",
  TIME_TO_FINANCING = "time_to_financing",
  PROFIT_AND_LOSS_PROGNOSIS = "profit_and_loss_prognosis",
}

export enum FieldRequestTypeEnum {
  COLLATERAL = "COLLATERAL",
  ADDITIONAL_INFORMATION = "ADDITIONAL_INFORMATION",
}

export enum RealEstateRepaymentOfLoanMethodEnum {
  REFINANCING = "REFINANCING",
  PROPERTY_REVENUE = "PROPERTY_REVENUE",
  COMPANY_REVENUE = "COMPANY_REVENUE",
  SALE_OF_PROPERTY = "SALE_OF_PROPERTY",
}

/**
 * Canonical field-request shape, consolidated from the former duplicates in
 * packages/customer-features/types/fieldRequest.ts (two type params) and this
 * file's own pre-KREA-3970 version (one type param).
 *
 * `submittedData` is non-nullable: since KREA-4444 the backend renamed
 * `FieldRequest.data` to `submitted_data` and declares it as a plain object on
 * every surface the customer app reads — `main/graphql/field_request/types.py`
 * returns `strawberry.scalars.JSON` (not `JSON | None`) and every
 * `FieldRequestDto` subclass in `main/dto/field_request_dtos.py` declares a
 * concrete, required `submitted_data`. An unfilled field request carries an
 * object whose own keys are null, and `isSubmitted` — not the absence of the
 * object — says whether the customer has answered. The whole field request can
 * still be `undefined` while the query is in flight, which is what the `?.`
 * reads at the call sites are for.
 *
 * GraphQL still exposes a deprecated `data` alias carrying the same object,
 * only so apps/admin can migrate separately; nothing here should select it.
 *
 * `F` defaults to this file's own enum but lets a consumer (e.g.
 * customer-features' FieldRequestFieldEnum) substitute its own without a
 * second interface.
 */
export interface IFieldRequest<
  T = never,
  C = never,
  F = FieldRequestFieldChoices,
> {
  id: string;
  type: FieldRequestTypeEnum;
  field: F;
  required: boolean;
  isSubmitted: boolean;
  isCollectedByLender: boolean;
  /** Despite the name, not "may the customer see this row": false rows still
   *  show on Erbjudanden and in the selected offer's Säkerheter summary. Set
   *  once, when the customer selects the offer: true only for the rows the
   *  post-select step asks the customer about. Fetched by the customer-features
   *  FieldRequestFragment; admin and lender queries don't select it. */
  isVisibleToCustomer?: boolean;
  lenderComment?: string | null;
  delegatedInformationResources?: {
    id: string;
    delegatedEmail: string;
  }[];
  displayName: string;
  subtitle?: string;
  displayText?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  improveApplicationDescription?: string;
  submittedData: T;
  config?: C | null;
}
